import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Loader2, MessageCircle, Send, X } from "lucide-react";
import { errMsg, publicPost } from "@/lib/api";

const WA = "393922910674";
const sid = () => {
  let s = localStorage.getItem("pgc_chat_sid");
  if (!s) { s = `s${Date.now()}${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem("pgc_chat_sid", s); }
  return s;
};

function Rich({ text }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return link[2].startsWith("/") ? <Link key={i} to={link[2]} className="font-semibold text-amber-300 underline">{link[1].startsWith("/") ? "Vedi prodotto" : link[1]}</Link> : <span key={i}>{link[1]}</span>;
    if (p.startsWith("**")) return <b key={i}>{p.slice(2, -2)}</b>;
    return <span key={i}>{p}</span>;
  });
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Ciao! Sono l'assistente di Club Prezzi. Chiedimi di punti, status, spedizioni o un prodotto che cerchi." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => {
    if (sessionStorage.getItem("pgc_hint_seen")) return;
    const t = setTimeout(() => { setHint(true); sessionStorage.setItem("pgc_hint_seen", "1"); }, 4000);
    const h = setTimeout(() => setHint(false), 16000);
    return () => { clearTimeout(t); clearTimeout(h); };
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const m = text.trim();
    if (!m || busy) return;
    setMsgs((x) => [...x, { role: "user", text: m }]);
    setText("");
    setBusy(true);
    try {
      const r = await publicPost("/chat", { session_id: sid(), message: m });
      setMsgs((x) => [...x, { role: "assistant", text: r.reply }]);
    } catch (err) {
      setMsgs((x) => [...x, { role: "assistant", text: errMsg(err) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-3 right-3 z-50 flex flex-col items-end gap-2 sm:bottom-4 sm:right-4 sm:gap-3">
      {open && (
        <div data-testid="chat-panel" className="flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#12141C] shadow-2xl">
          <div className="flex items-center justify-between border-b border-white/10 bg-[#0B0C10] px-4 py-3">
            <p className="flex items-center gap-2 font-semibold text-white"><Bot className="h-5 w-5 text-amber-400" /> Assistente del club <span className="flex items-center gap-1 text-[10px] font-normal text-emerald-400"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />online</span></p>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "ml-auto bg-amber-500 text-black" : "bg-white/5 text-slate-200"}`}>
                <Rich text={m.text} />
              </div>
            ))}
            {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-3">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Scrivi un messaggio…" className="flex-1 rounded-full bg-[#1C1F2C] px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none" />
            <button data-testid="chat-send" disabled={busy} className="grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-black disabled:opacity-60"><Send className="h-4 w-4" /></button>
          </form>
          <p className="px-4 pb-2 text-[10px] text-slate-500">Risposte generate da AI. Non inserire dati sensibili.</p>
        </div>
      )}
      {!open && hint && (
        <div data-testid="chat-hint" className="fade-up relative max-w-[240px] rounded-2xl rounded-br-sm border border-amber-500/40 bg-[#12141C] px-4 py-3 text-sm text-white shadow-2xl">
          <button onClick={() => setHint(false)} className="absolute right-2 top-1 text-slate-500 hover:text-white" aria-label="Chiudi">×</button>
          <p className="flex items-center gap-2 font-semibold"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> Siamo online!</p>
          <p className="mt-1 text-slate-300">Hai domande su punti, prodotti o spedizioni? Scrivici in chat o su WhatsApp.</p>
        </div>
      )}
      <a data-testid="whatsapp-button" href={`https://wa.me/${WA}?text=${encodeURIComponent("Ciao Club Prezzi, vorrei informazioni")}`} target="_blank" rel="noreferrer"
        className="group relative grid h-12 w-12 place-items-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-110 sm:h-14 sm:w-14" aria-label="Scrivici su WhatsApp">
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-40 [animation-duration:2s]" style={{ clipPath: "circle(50%)" }} />
        <span className="pointer-events-none absolute right-16 hidden whitespace-nowrap rounded-full bg-[#25D366] px-3 py-1 text-xs font-semibold text-white group-hover:block">Scrivici su WhatsApp</span>
        <svg viewBox="0 0 24 24" className="relative h-7 w-7 fill-current"><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.04 21.5h-.01a9.45 9.45 0 01-4.82-1.32l-.35-.21-3.58.94.96-3.49-.23-.36a9.43 9.43 0 01-1.45-5.03c0-5.22 4.25-9.47 9.48-9.47a9.4 9.4 0 016.7 2.78 9.4 9.4 0 012.77 6.7c0 5.23-4.25 9.46-9.47 9.46zm8.06-17.53A11.33 11.33 0 0012.04.63C5.76.63.65 5.74.65 12.02c0 2 .52 3.96 1.52 5.69L.55 23.62l6.04-1.58a11.36 11.36 0 005.44 1.39h.01c6.28 0 11.39-5.11 11.39-11.39 0-3.04-1.18-5.9-3.33-8.05z" /></svg>
      </a>
      <button data-testid="chat-toggle" onClick={() => { setOpen(!open); setHint(false); }} className={`relative grid h-12 w-12 place-items-center rounded-full bg-amber-500 text-black shadow-lg transition-transform hover:scale-110 sm:h-14 sm:w-14 ${open ? "" : "animate-bounce [animation-duration:2.5s]"}`} aria-label="Apri la chat">
        {!open && <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-[#0B0C10] bg-emerald-400" />}
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
}
